import React, { useEffect, useState } from 'react';
import { Camera, ImageOff } from 'lucide-react';
import api from '../../utils/api';

// Client-facing recap photo preview. City/state-safe: shows only the photos
// talent uploaded for the event (job_photos + recap_photos) with their shot
// labels — never any personal/location data.
export const EventPhotosPreview = ({ shiftId, compact = false }) => {
  const [photos, setPhotos] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!shiftId) return;
    (async () => {
      try {
        const res = await api.get(`/recaps/client/event-photos/${shiftId}`);
        const all = [...(res.data.job_photos || []), ...(res.data.recap_photos || [])];
        if (alive) setPhotos(all);
      } catch {
        if (alive) setPhotos([]);
      }
    })();
    return () => { alive = false; };
  }, [shiftId]);

  if (photos === null) {
    return <div className="text-xs text-slate-400 py-4" data-testid="event-photos-loading">Loading event photos…</div>;
  }
  if (photos.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-4" data-testid="event-photos-empty">
        <ImageOff className="w-4 h-4" /> No event photos uploaded yet.
      </div>
    );
  }

  return (
    <div data-testid="event-photos-preview">
      <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-slate-700">
        <Camera className="w-4 h-4 text-cyan-600" /> Event Photos
        <span className="text-xs font-normal text-slate-400">({photos.length})</span>
      </div>
      <div className={`grid gap-2 ${compact ? 'grid-cols-4 sm:grid-cols-6' : 'grid-cols-3 sm:grid-cols-5'}`}>
        {photos.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActive(p)}
            className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group"
            data-testid={`event-photo-${p.id}`}
          >
            {p.media_type === 'video' ? (
              <video src={p.photo_url} className="w-full h-full object-cover" muted />
            ) : (
              <img src={p.photo_url} alt={p.caption || 'Event photo'} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
            )}
            {p.shot_category && (
              <span className="absolute bottom-1 left-1 text-[9px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded-full">
                {p.shot_category}
              </span>
            )}
          </button>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="event-photo-lightbox"
        >
          <div className="max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            {active.media_type === 'video' ? (
              <video src={active.photo_url} controls autoPlay className="max-h-[80vh] rounded-lg" />
            ) : (
              <img src={active.photo_url} alt={active.caption || 'Event photo'} className="max-h-[80vh] rounded-lg" />
            )}
            <div className="text-center text-white/80 text-xs mt-2">
              {active.shot_category ? `${active.shot_category} · ` : ''}{active.uploaded_by || ''}
              {active.caption ? ` — ${active.caption}` : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPhotosPreview;
